import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, AuthShell, Button, Link, Stack, TextField, Typography, } from '@platform/design-system';
import { useSession } from "../auth/session.js";
import { AuthFlowError } from "../auth/types.js";
import { describeError } from "../http/errors.js";
import { RouterLink } from "../router/router-link.js";
import { ConfirmEmailStep } from "./auth-steps.js";
import { emailSchema, PASSWORD_HINT, PASSWORDS_MATCH, passwordSchema } from "./password-rules.js";
import { authShellFrame } from "./sign-in-screen.js";
const detailsForm = z
    .object({
    givenName: z.string().trim().max(64, 'Use 64 characters or fewer'),
    familyName: z.string().trim().max(64, 'Use 64 characters or fewer'),
    email: emailSchema,
    password: passwordSchema,
    confirm: z.string(),
})
    .refine(...PASSWORDS_MATCH);
/**
 * Sign-up for a PERSONAL product (ADR *personal-tenants-sign-up-in-the-cell-pool*): the person
 * creates their own identity in the cell pool — details → the emailed code → an automatic SRP
 * sign-in with the password they just chose. The password goes only to the pool; it is held in
 * memory between the two steps and dropped as soon as the sign-in answers. No workspace, no
 * address, no invitation: the plan is chosen next, on the app's own page.
 */
export function PersonalSignUpScreen(props) {
    const { frame, onSignedIn, onConfirmed } = props;
    const signInHref = props.signInHref ?? '/login';
    const session = useSession();
    const [step, setStep] = useState(props.verifyEmail
        ? { kind: 'code', email: props.verifyEmail, destination: null }
        : { kind: 'details' });
    const [signingIn, setSigningIn] = useState(false);
    const [exists, setExists] = useState(false);
    const credentials = useRef(null);
    const form = useForm({
        resolver: zodResolver(detailsForm),
        defaultValues: { givenName: '', familyName: '', email: '', password: '', confirm: '' },
    });
    /** After the code: sign in over SRP with the password still in memory, then forget it. */
    const signInNow = async (email) => {
        const c = credentials.current;
        credentials.current = null;
        if (!c || c.email !== email) {
            onConfirmed(email);
            return;
        }
        setSigningIn(true);
        try {
            const result = await session.signIn(c);
            if (result.kind === 'done')
                onSignedIn(result.principal);
            else
                onConfirmed(email);
        }
        catch {
            onConfirmed(email);
        }
        finally {
            setSigningIn(false);
        }
    };
    const createAccount = async (v) => {
        setExists(false);
        try {
            const result = await session.signUp({
                email: v.email,
                password: v.password,
                ...(v.givenName ? { givenName: v.givenName } : {}),
                ...(v.familyName ? { familyName: v.familyName } : {}),
            });
            credentials.current = { email: v.email, password: v.password };
            if (result.kind === 'done')
                await signInNow(v.email);
            else
                setStep({ kind: 'code', email: v.email, destination: result.destination });
        }
        catch (e) {
            if (e instanceof AuthFlowError && e.code === 'account-exists')
                setExists(true);
            else if (e instanceof AuthFlowError && e.code === 'password-policy')
                form.setError('password', { message: e.message });
            else
                form.setError('root', { message: describeError(e, 'We could not create the account.') });
        }
    };
    if (step.kind === 'code') {
        return (_jsx(AuthShell, { ...authShellFrame(frame), title: "Check your email", lead: `We sent a 6-digit code to ${step.destination ?? step.email}. Enter it to confirm your email.`, children: _jsxs(Stack, { spacing: 2, children: [signingIn && (_jsx(Typography, { variant: "body2", color: "text.secondary", role: "status", children: "Email confirmed. Signing you in\u2026" })), _jsx(ConfirmEmailStep, { email: step.email, busy: signingIn, onConfirmed: () => void signInNow(step.email) }), !props.verifyEmail && (_jsx(Button, { variant: "text", size: "small", disabled: signingIn, onClick: () => {
                            credentials.current = null;
                            form.resetField('password');
                            form.resetField('confirm');
                            setStep({ kind: 'details' });
                        }, sx: { alignSelf: 'flex-start' }, children: "Use a different email" }))] }) }));
    }
    const rootError = form.formState.errors.root?.message;
    return (_jsx(AuthShell, { ...authShellFrame(frame), title: "Create your account", lead: "Your account is for you alone. You choose your plan next.", children: _jsx("form", { onSubmit: (e) => void form.handleSubmit(createAccount)(e), noValidate: true, children: _jsxs(Stack, { spacing: 2, children: [props.notice, rootError && _jsx(Alert, { severity: "error", children: rootError }), exists && (_jsxs(Alert, { severity: "info", role: "status", children: ["An account with this email already exists.", ' ', _jsx(Link, { component: RouterLink, href: `${signInHref}?email=${encodeURIComponent(form.getValues('email'))}`, children: "Sign in instead" }), "."] })), _jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, spacing: 2, children: [_jsx(TextField, { label: "First name", autoComplete: "given-name", hint: "Optional", ...form.register('givenName'), error: form.formState.errors.givenName?.message }), _jsx(TextField, { label: "Last name", autoComplete: "family-name", hint: "Optional", ...form.register('familyName'), error: form.formState.errors.familyName?.message })] }), _jsx(TextField, { label: "Email", type: "email", autoComplete: "email", ...form.register('email'), error: form.formState.errors.email?.message, hint: "We email a code here to confirm it." }), _jsx(TextField, { label: "Password", type: "password", autoComplete: "new-password", ...form.register('password'), error: form.formState.errors.password?.message, hint: PASSWORD_HINT }), _jsx(TextField, { label: "Confirm password", type: "password", autoComplete: "new-password", ...form.register('confirm'), error: form.formState.errors.confirm?.message }), _jsx(Button, { type: "submit", variant: "contained", size: "large", busy: form.formState.isSubmitting, busyLabel: "Creating\u2026", children: "Create account" }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["Already have an account?", ' ', _jsx(Link, { component: RouterLink, href: signInHref, variant: "body2", children: "Sign in" })] })] }) }) }));
}
//# sourceMappingURL=personal-sign-up-screen.js.map